import { Fragment, MouseEvent, useEffect, useState } from 'react';
import getConfig from 'next/config';
import classnames from 'classnames';

import { CloseOutlined } from '@JohnLewisPartnership/jl-design-system/components/icons/CloseOutlined';
import AddToBasketButton from '@JohnLewisPartnership/jl-design-system/components/add-to-basket-button/AddToBasketButton';

import {
  getBrand,
  isAnydayBrand,
} from '@JohnLewisPartnership/jl-design-system/utils/brands/brands';
import constants from '../../../../utils/constants';

import { useMedia } from '../../../../hooks/useMedia';
import InfoSection from '../../../info-section/InfoSection';
import Title from '../../../title/Title';
import AddToBasketError from './add-to-basket-error/AddToBasketError';
import GoToBasket from './go-to-basket/GoToBasket';
import ViewOptions from './view-options/ViewOptions';
import { shouldShowAddToBasketButton } from '../../../../utils/shared/shouldShowAddToBasketButton';
import { openMiniBasket } from '../../../../utils/basket/openMiniBasket';
import styles from './CompareHeader.module.scss';
import {
  ADD_TO_BASKET,
  dispatchImmediateAnalyticsEvent,
} from '../../../analytics/analyticsEvents';
import { trackSingleSkuAddToBasket } from '../../../analytics/jlAnalytics';
import { mapProduct } from '../../../analytics/utils/mapProduct';
import { EnrichedCompareProduct } from '../../../../utils/types/types';
import { formatAmplienceImageURL } from '@JohnLewisPartnership/jl-design-system/utils/imageUrlModifier/imageUrlModifier';
import { useCompareHeaderData } from './data/useCompareHeaderData';

type Props = {
  products: EnrichedCompareProduct[];
  maxCompare: number;
};

const { publicRuntimeConfig } = getConfig();
const { XXS_MOBILE_MAX_WIDTH, XS_MOBILE_MAX_WIDTH, MOBILE_MAX_WIDTH } =
  constants;

export const stripAnydayBrandFromTitle = (productTitle) =>
  productTitle.replace(
    new RegExp(/^anyday john lewis & partners|^john lewis anyday/, 'i'),
    ''
  );

export const CompareHeader = ({ products, maxCompare }: Props) => {
  const ctaStyle = useMedia(
    [
      `(min-width: ${MOBILE_MAX_WIDTH}px)`,
      `(min-width: ${XS_MOBILE_MAX_WIDTH}px)`,
      `(min-width: ${XXS_MOBILE_MAX_WIDTH}px)`,
      '(min-width: 0px)',
    ],
    ['full', 'medium', 'small', 'tiny'],
    'full'
  );

  const isMobileWidth = ctaStyle === 'small';
  const {
    compare,
    cachedBasket,
    experiences,
    breadcrumbContext,
    getAnalyticsProductData,
  } = useCompareHeaderData();
  const { atbBlockList, isNewAtbAnalyticsFlagOn } = experiences;
  const [addToBasketErrors, setAddToBasketErrors] = useState([]);
  const allProducts = products.slice(0, maxCompare);
  const numProducts = allProducts.length;
  const { MAX_COMPARE_MOBILE } = constants;
  const { breadcrumbs } = breadcrumbContext;

  const handleGoToBasket = (event: MouseEvent<HTMLButtonElement>) => {
    compare.close();
    openMiniBasket(event);
  };

  const handleAddToBasketError = (sku: string) => () => {
    setAddToBasketErrors(addToBasketErrors.concat(sku));
    return Promise.resolve();
  };

  useEffect(() => {
    if (compare.loading || allProducts.length === 0) {
      return;
    }
  }, [compare.loading, allProducts]);

  if (compare.loading || !numProducts) {
    return null;
  }

  return (
    <Fragment>
      <div className={styles.container} data-testid="compare-header">
        {allProducts.map((product) => {
          const { name, price, productId, url } = product;

          const onRemoveClick = () => {
            compare.toggle(productId, undefined);
          };

          const image = formatAmplienceImageURL(
            `${product?.image?.source}?wid=64`
          );

          const isAnydayProduct = isAnydayBrand(name);
          const productTitle = isAnydayProduct
            ? stripAnydayBrandFromTitle(name)
            : name;
          const brand = isAnydayProduct
            ? {
                id: 'anyday',
                name: 'ANYDAY',
                namePrefix: 'John Lewis ',
                titleWithoutBrandName: productTitle,
              }
            : getBrand(name);

          return (
            <div key={productId} className={styles.product}>
              {image && (
                <a className={styles.imageLink} href={url} aria-hidden="true">
                  <img
                    className={styles.image}
                    src={image}
                    alt={productTitle}
                  />
                </a>
              )}

              <div className={styles.productInfo}>
                <a className={styles.productInfoLink} href={url}>
                  <Title title={productTitle} brand={brand} />
                  {price &&
                    (brand ? (
                      <InfoSection
                        otherClasses={classnames(
                          styles[`price--${brand.id}`],
                          styles.price
                        )}
                      >
                        {price}
                      </InfoSection>
                    ) : (
                      <InfoSection otherClasses={styles.price}>
                        {price}
                      </InfoSection>
                    ))}
                </a>
              </div>

              <button
                aria-label="Remove product"
                className={styles.removeButton}
                data-testid="compare-remove-button"
                key={`remove-${productId}`}
                onClick={onRemoveClick}
              >
                <CloseOutlined title={`Remove ${productTitle}`} size="medium" />
              </button>
            </div>
          );
        })}
      </div>

      <div className={styles.ctaButtons}>
        {allProducts.map((product) => {
          const { productId, skuId, url, defaultVariantId, name } = product;
          const isAddToBasketVisible = shouldShowAddToBasketButton(
            product,
            atbBlockList
          );
          const isThisSkuInBasket = cachedBasket.products.some(
            (p) => p.sku === skuId
          );
          const hasErred = addToBasketErrors.includes(skuId);
          const shouldUseShortestButtonText =
            isMobileWidth && numProducts === MAX_COMPARE_MOBILE;

          const productEventData = mapProduct(product);
          const singleSkuAtbAnalytics = () => {
            dispatchImmediateAnalyticsEvent(ADD_TO_BASKET, {
              products: productEventData,
            });
            if (isNewAtbAnalyticsFlagOn) {
              trackSingleSkuAddToBasket(
                getAnalyticsProductData(productId),
                breadcrumbs,
                cachedBasket
              );
            }
          };

          return (
            <div className={styles.ctaColumn} key={productId}>
              {hasErred && (
                <AddToBasketError
                  pdpUrl={url}
                  data-testid="compare-add-to-basket-error"
                />
              )}
              <div className={styles.ctaButton}>
                {isAddToBasketVisible && isThisSkuInBasket && !hasErred && (
                  <GoToBasket
                    goToBasket={handleGoToBasket}
                    veryShortCtaText={shouldUseShortestButtonText}
                    data-testid="compare-go-to-basket"
                  />
                )}
                {isAddToBasketVisible && !isThisSkuInBasket && !hasErred && (
                  <AddToBasketButton
                    skuId={skuId}
                    variantId={defaultVariantId}
                    title={name}
                    clientId="jl-standard-plp"
                    shortCtaText
                    customText={shouldUseShortestButtonText ? 'Add' : undefined}
                    data-testid="compare-add-to-basket-button"
                    path={publicRuntimeConfig.basketBFFService}
                    onError={handleAddToBasketError(skuId)}
                    onSuccess={singleSkuAtbAnalytics}
                    className={styles.atbMinimalPadding}
                  />
                )}
                {!isAddToBasketVisible && (
                  <ViewOptions
                    pdpUrl={url}
                    veryShortCtaText={shouldUseShortestButtonText}
                    data-testid="compare-view-options"
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Fragment>
  );
};
